/**
 * Shared glass surfaces for the Vite /admin SPA.
 * Keep hierarchy, contrast, and spacing consistent across dashboard pages.
 */

import type { ElementType, ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

export function AdminGlassCard({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-white/[0.07] bg-white/[0.035] shadow-[0_1px_0_rgba(255,255,255,0.03)_inset] backdrop-blur-sm",
        className,
      )}
      data-admin-surface="glass-card"
    >
      {children}
    </div>
  );
}

export function AdminSectionLabel({
  icon: Icon,
  title,
  description,
  className,
}: {
  icon: ElementType;
  title: string;
  description?: string;
  className?: string;
}) {
  return (
    <div className={cn("mb-3 flex items-center gap-2.5", className)}>
      <div className="flex items-center justify-center rounded-xl border border-white/[0.07] bg-white/[0.04] p-1.5">
        <Icon className="h-3.5 w-3.5 text-white/50" strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold tracking-tight text-white/80">{title}</p>
        {description ? (
          <p className="text-xs leading-relaxed text-white/40">{description}</p>
        ) : null}
      </div>
    </div>
  );
}

type AdminKpiCardProps = {
  label: string;
  value: string | null;
  hint?: string | null;
  subValue?: string | null;
  loading?: boolean;
  to?: string;
  icon?: ElementType;
  iconColor?: string;
  glowColor?: string;
  /** Left accent bar for gateway identity */
  accent?: "stripe" | "mp" | "neutral";
  className?: string;
};

/** Required so Tailwind emits a real ::before box for gateway accent bars. */
export const ADMIN_KPI_ACCENT_BEFORE =
  "before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:content-['']";

const ACCENT_BAR: Record<NonNullable<AdminKpiCardProps["accent"]>, string> = {
  stripe: "before:bg-[hsl(220_90%_60%)]",
  mp: "before:bg-[hsl(187_85%_53%)]",
  neutral: "before:bg-white/20",
};

/** Full accent class string applied on AdminKpiCard (testable). */
export function adminKpiAccentClass(
  accent: NonNullable<AdminKpiCardProps["accent"]>,
): string {
  return `${ADMIN_KPI_ACCENT_BEFORE} ${ACCENT_BAR[accent]}`;
}

export function AdminKpiCard({
  label,
  value,
  hint,
  subValue,
  loading,
  to,
  icon: Icon,
  iconColor = "text-white/55",
  glowColor = "bg-white/10",
  accent,
  className,
}: AdminKpiCardProps) {
  const body = (
    <AdminGlassCard
      className={cn(
        "relative h-full overflow-hidden p-4 transition-all duration-200 sm:p-5",
        "hover:border-white/12 hover:bg-white/[0.05]",
        accent && adminKpiAccentClass(accent),
        className,
      )}
    >
      {glowColor ? (
        <div
          className={cn(
            "pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full opacity-25 blur-2xl",
            glowColor,
          )}
        />
      ) : null}
      <div className={cn("relative", accent && "pl-1")}>
        <div className="mb-3 flex items-start justify-between gap-2">
          {Icon ? (
            <div className="inline-flex items-center justify-center rounded-xl border border-white/[0.08] bg-white/[0.04] p-2.5">
              <Icon className={cn("h-4 w-4", iconColor)} strokeWidth={1.75} />
            </div>
          ) : (
            <span />
          )}
        </div>
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40">
          {label}
        </p>
        {loading || value === null ? (
          <Skeleton className="h-8 w-28 rounded-xl bg-white/[0.07]" />
        ) : (
          <p className="text-xl font-semibold tabular-nums tracking-tight text-white/95 sm:text-2xl">
            {value}
          </p>
        )}
        {subValue ? (
          <p className="mt-1 text-xs tabular-nums text-white/45">{subValue}</p>
        ) : null}
        {loading && hint === null ? (
          <Skeleton className="mt-2 h-3 w-24 rounded bg-white/[0.06]" />
        ) : hint ? (
          <p className="mt-1.5 text-[11px] leading-snug text-white/40">{hint}</p>
        ) : null}
        {to ? (
          <p className="mt-2.5 flex items-center gap-0.5 text-[10px] font-medium text-white/35 transition-colors group-hover:text-white/60">
            Ver detalle <ArrowRight className="h-3 w-3" />
          </p>
        ) : null}
      </div>
    </AdminGlassCard>
  );

  if (to) {
    return (
      <Link to={to} className="group block h-full focus-visible:outline-none">
        {body}
      </Link>
    );
  }
  return body;
}

export function AdminEmptyState({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-h-[88px] items-center justify-center rounded-xl border border-dashed border-white/[0.08] bg-white/[0.02] px-4 py-6 text-center text-xs leading-relaxed text-white/40",
        className,
      )}
      data-admin-surface="empty"
    >
      {children}
    </div>
  );
}

export function AdminPanel({
  children,
  className,
  title,
  description,
  action,
  icon: Icon,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
  description?: string;
  action?: ReactNode;
  icon?: ElementType;
}) {
  return (
    <AdminGlassCard className={cn("p-4 sm:p-5", className)}>
      {(title || action) && (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2 border-b border-white/[0.05] pb-3">
          <div className="min-w-0">
            {title ? (
              <div className="flex items-center gap-2">
                {Icon ? (
                  <Icon className="h-4 w-4 shrink-0 text-white/50" strokeWidth={1.75} />
                ) : null}
                <p className="text-sm font-semibold tracking-tight text-white/85">{title}</p>
              </div>
            ) : null}
            {description ? (
              <p className="mt-1 text-xs leading-relaxed text-white/40">{description}</p>
            ) : null}
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      )}
      {children}
    </AdminGlassCard>
  );
}
